import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  Modal,
  Form,
  InputNumber,
  Input,
  Select,
  DatePicker,
  Alert,
  message
} from 'antd'
import { postRequest, dutyTransactionLogManualUrl } from '../../config'

const METHOD_OPTIONS = [
  { value: 'CASH', label: 'Cash / Offline' },
  { value: 'MTN_MOMO', label: 'MTN MoMo' },
  { value: 'BANK_TRANSFER', label: 'Bank transfer' },
  { value: 'PAYSTACK', label: 'Paystack' }
]

const STATUS_OPTIONS = [
  { value: 'SUCCESSFUL', label: 'Successful (paid)' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'FAILED', label: 'Failed' }
]

// Log a duty payment made outside the assistant — a walk-in or a customer who paid in your DM
// instead of using WhatsApp. Only the amount is required; adding a phone links it to a customer so
// it also shows in customer analytics.
const LogPaymentModal = ({ open, onClose, onLogged }) => {
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)

  const onFinish = async values => {
    const payload = {
      amount: values.amount,
      currency: values.currency || 'GHS',
      method: values.method || 'CASH',
      status: values.status || 'SUCCESSFUL',
      note: values.note || '',
      customerPhone: values.customerPhone || '',
      customerName: values.customerName || '',
      carDescription: values.carDescription || '',
      paidAt: values.paidAt ? values.paidAt.toISOString() : null
    }
    const res = await postRequest(
      dutyTransactionLogManualUrl,
      payload,
      'payment',
      setSaving
    )
    if (res.success) {
      message.success('Payment logged')
      form.resetFields()
      onLogged && onLogged()
      onClose()
    } else if (res.error && res.error.response && res.error.response.data) {
      const data = res.error.response.data
      form.setFields(
        Object.keys(data).map(k => ({
          name: k,
          errors: [Array.isArray(data[k]) ? data[k][0] : String(data[k])]
        }))
      )
    }
  }

  return (
    <Modal
      title="Log a payment"
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Log payment"
      confirmLoading={saving}
      destroyOnClose
    >
      <Alert
        type="info"
        message="For customers who paid directly (walk-in / DM) instead of using the WhatsApp assistant. Adding a phone links it to a customer and shows it in customer analytics."
        style={{ marginBottom: 16 }}
      />
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ currency: 'GHS', method: 'CASH', status: 'SUCCESSFUL' }}
      >
        <Form.Item
          name="amount"
          label="Amount"
          rules={[{ required: true, message: 'Enter the amount paid.' }]}
        >
          <InputNumber min={0} style={{ width: '100%' }} addonBefore="GHS" />
        </Form.Item>
        <Form.Item name="method" label="Method">
          <Select options={METHOD_OPTIONS} />
        </Form.Item>
        <Form.Item name="status" label="Status">
          <Select options={STATUS_OPTIONS} />
        </Form.Item>
        <Form.Item
          name="customerPhone"
          label="Customer phone (optional)"
          extra="Links the payment to a customer + shows it in customer analytics."
        >
          <Input placeholder="0244000000" />
        </Form.Item>
        <Form.Item name="customerName" label="Customer name (optional)">
          <Input placeholder="e.g. Kofi Mensah" />
        </Form.Item>
        <Form.Item name="carDescription" label="Car (optional)">
          <Input placeholder="e.g. 2016 Toyota Corolla from USA" />
        </Form.Item>
        <Form.Item
          name="paidAt"
          label="Paid on (optional)"
          extra="Leave blank for now. Set it to record a payment from a past date."
        >
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="note" label="Note (optional)">
          <Input.TextArea rows={2} placeholder="Any context about this payment" />
        </Form.Item>
      </Form>
    </Modal>
  )
}

LogPaymentModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onLogged: PropTypes.func
}

export default LogPaymentModal
